// crewbit-admin/src/components/money/proposals/ProposalConfidenceMeter.tsx
//
// Tier 2-UI Phase B Phase B (admin) — confidence display primitive. Mirrors
// crewbit-mobile/components/money/proposals/ProposalConfidenceMeter.tsx body
// decisions per `feedback_admin_pair_must_match_mobile_body`. Three-tier
// mapping (boundaries identical to mobile):
//   ≥ 0.9    high   — accent tone
//   0.6..0.9 medium — accent muted tone
//   < 0.6    low    — amber + "please review" hint
//
// Tier boundaries: confidence < 0.6 = LOW; 0.6 ≤ confidence < 0.9 = MEDIUM;
// confidence ≥ 0.9 = HIGH.

import { palette, radii, spacing, typography } from '../../../styles/tokens';

type ConfidenceTier = 'low' | 'medium' | 'high';

function tierFromConfidence(confidence: number): ConfidenceTier {
  if (confidence >= 0.9) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

interface Props {
  confidence: number;
}

const LABELS: Record<ConfidenceTier, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence — please review',
};

interface Tone {
  text: string;
  fill: string;
}

const TONE: Record<ConfidenceTier, Tone> = {
  high: { text: palette.accent, fill: palette.accent },
  medium: { text: palette.accent, fill: palette.accentSoft },
  low: { text: palette.amber, fill: palette.amber },
};

export default function ProposalConfidenceMeter({ confidence }: Props) {
  const tier = tierFromConfidence(confidence);
  const tone = TONE[tier];
  const widthPct = Math.max(8, Math.min(100, Math.round(confidence * 100)));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
        paddingBlock: spacing.xs,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: typography.weight.semibold,
          color: tone.text,
          fontFamily: typography.fontFamily,
        }}
      >
        {LABELS[tier]}
      </div>
      <div
        style={{
          height: 6,
          background: palette.g100,
          borderRadius: radii.sm,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${widthPct}%`,
            background: tone.fill,
            borderRadius: radii.sm,
          }}
        />
      </div>
    </div>
  );
}
